import { useSearchParams } from "react-router";
import { useConfigsApi } from "src/api/useConfigsApi";
import { useUserProfile } from "src/auth/queries";
import { isLoggedIn } from "src/auth/utils/session";
import {
  getCurrencyCodeFromCookie,
  type SupportedCurrencyCode,
} from "src/utils/currency";
import { getDistanceSystemFromCookie } from "src/utils/distanceSystem";
import { getInitialTimeFormatCookieValue } from "src/utils/timeFormat";

export type UserSettings = {
  status: "success" | "pending" | "error";
} & ResolvedUserSettings;

type ResolvedUserSettings = {
  clockType: "clock12" | "clock24";
  distanceSystem: "imperial" | "metric";
  currencyCode: SupportedCurrencyCode;
  prefers12Hour: boolean;
};

export function useUserSettings(): UserSettings {
  const userSettingOverrides = useUserSettingOverrides();

  const { data: userSettings, status: userStatus } = useUserProfile(
    (data) =>
      ({
        clockType: data.is24h ? "clock24" : "clock12",
        distanceSystem: data.isKilometers ? "metric" : "imperial",
        currencyCode: data.profileCurrency,
        prefers12Hour: !data.is24h,
      }) satisfies ResolvedUserSettings,
  );

  const { data: configSettings, status: configStatus } = useConfigsApi(
    (data) =>
      ({
        clockType: data.clockType,
        distanceSystem: data.distanceUnit === "km" ? "metric" : "imperial",
        currencyCode: data.currencyCode,
        prefers12Hour: data.clockType === "clock12",
      }) satisfies ResolvedUserSettings,
  );

  if (isLoggedIn()) {
    return {
      status: userStatus,
      ...placeholderUserSettings(),
      ...userSettings,
      ...userSettingOverrides,
    };
  } else {
    return {
      status: configStatus,
      ...placeholderUserSettings(),
      ...configSettings,
      ...userSettingOverrides,
    };
  }
}

function placeholderUserSettings(): ResolvedUserSettings {
  const prefers12Hour = getInitialTimeFormatCookieValue();
  let clockType: "clock12" | "clock24";
  if (prefers12Hour) {
    clockType = "clock12";
  } else {
    clockType = "clock24";
  }

  return {
    clockType: clockType ?? "clock24",
    currencyCode: getCurrencyCodeFromCookie() ?? "USD",
    distanceSystem: getDistanceSystemFromCookie() ?? "metric",
    prefers12Hour: prefers12Hour ?? false,
  };
}

function useUserSettingOverrides(): Partial<ResolvedUserSettings> {
  const [searchParams] = useSearchParams();

  const result: Partial<UserSettings> = {};

  const prefers12Hour = searchParams.get("prefers12Hour");
  if (prefers12Hour) {
    result.prefers12Hour = prefers12Hour === "true";
  }

  const currencyCode = searchParams.get("currency") as SupportedCurrencyCode;
  if (currencyCode) {
    result.currencyCode = currencyCode;
  }

  const distanceParam = searchParams.get("distance");
  if (distanceParam) {
    result.distanceSystem = distanceParam === "km" ? "metric" : "imperial";
  }

  return result;
}
